import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import NavItem from "./NavItem";
import NotificationBell from "./NotificationBell";
import DropDownMenu from "../UIElements/DropDownMenu";
import DropDownItem from "../UIElements/DropDownItem";
import { AuthContext } from "../../context/auth-context";
import { useNavigate } from "react-router-dom";
import { normalizeEmail } from "../../util/contactInfo";
import { confirmNavigation } from "../../utils/navigationGuard";

import "./styling/NavLinks.css";

const NavLinks = (props) => {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [showUserDropDown, setShowUserDropDown] = useState(false);
  const dropdownRef = useRef(null);

  // Resolve profile image URL (mirrors NavItem logic)
  const profileImageSrc = useMemo(() => {
    const imageUrl = auth.user?.profileImageUrl;
    if (!imageUrl) return null;
    if (imageUrl.startsWith('s3://')) {
      const m = imageUrl.match(/^s3:\/\/([^\/]+)\/(.+)$/);
      if (m) return `https://${m[1]}.s3.amazonaws.com/${m[2]}`;
    }
    return imageUrl.includes('://')
      ? imageUrl
      : `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${imageUrl}`;
  }, [auth.user?.profileImageUrl]);
  
  // Define dropdown close handler before useEffect
  const handleDropdownClose = useCallback(() => {
    setShowUserDropDown(false);
    if (props.onUserDropdownToggle) {
      props.onUserDropdownToggle(false);
    }
  }, [props]);
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (showUserDropDown) {
          handleDropdownClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropDown, handleDropdownClose]);

  const logoutHandler = (event) => {
    if (!confirmNavigation()) return;
    setShowUserDropDown(false);
    auth.setSelectedProject(null);
    // Explicit user logout: also revoke the refresh token and end the
    // Maestro login session (cookie), so Sign In re-prompts for credentials.
    auth.logout({ endServerSession: true });
  };

  const settingsHandler = () => {
    if (!confirmNavigation()) return;
    setShowUserDropDown(false);
    navigate("/user-settings");
  };
  
  const closeDropDownHandler = () => {
    setShowUserDropDown(false);
  };

  const toggleUserDropdown = () => {
    const newState = !showUserDropDown;
    setShowUserDropDown(newState);
    if (props.onUserDropdownToggle) {
      props.onUserDropdownToggle(newState);
    }
  };

  const handleSettingsClick = () => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    navigate("/user-settings");
  };

  const handleHelpClick = () => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    navigate("/help");
  };

  const handleLogoutClick = (event) => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    auth.setSelectedProject(null);
    auth.logout({ endServerSession: true });
  };

  const handleProjectsClick = () => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    if (auth.selectedProject) {
      auth.setSelectedProject(null);
    }
    navigate("/projects");
  };

  // Cross-project page — deliberately keeps the selected project (unlike
  // Projects/Companies) so popping over to check tasks doesn't drop the user's
  // working context. The project sidebar is hidden on /tasks instead.
  const handleMyTasksClick = () => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    navigate("/tasks");
  };

  const handleClientsClick = () => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    if (auth.selectedProject) {
      auth.setSelectedProject(null);
    }
    navigate("/clients");
  };

  const handleAdminClick = () => {
    if (!confirmNavigation()) return;
    handleDropdownClose();
    if (auth.selectedProject) {
      auth.setSelectedProject(null);
    }
    navigate("/admin/companies");
  };

  // Memoize user initials to prevent recalculation on every render
  const userInitials = useMemo(() => {
    if (!auth.user) return '';
    const firstInitial = auth.user.firstName?.[0] || '';
    const lastInitial = auth.user.lastName?.[0] || '';
    return `${firstInitial}${lastInitial}`;
  }, [auth.user?.firstName, auth.user?.lastName]);

  const { isDrawer, contextNav } = props;

  return (
    <ul className="nav-links">

      {/* ── Drawer: user card at top ─────────────────────────── */}
      {isDrawer && auth.isLoggedIn && (
        <li className="drawer-user-header">
          <div className="drawer-user-avatar">
            {profileImageSrc ? (
              <img src={profileImageSrc} alt="Profile" className="drawer-user-photo" />
            ) : userInitials ? (
              <div className="drawer-user-initials">{userInitials}</div>
            ) : (
              <div className="drawer-user-initials">👤</div>
            )}
          </div>
          <div className="drawer-user-info">
            <span className="drawer-user-name">
              {auth.user?.firstName} {auth.user?.lastName}
            </span>
            {auth.user?.email && (
              <span className="drawer-user-email">{normalizeEmail(auth.user.email)}</span>
            )}
          </div>
        </li>
      )}

      {/* ── Drawer: section navigation, directly under the user card ── */}
      {/* Mobile only — MainNavigation passes the embedded ProjectSidebar (or
          AdminSidebar on /admin routes) here, so the single header ☰ opens the
          section nav and the global nav together. */}
      {isDrawer && contextNav && (
        <li className="drawer-context-nav-slot">{contextNav}</li>
      )}

      {/* ── Nav links ────────────────────────────────────────── */}
      {!auth.isLoggedIn && <NavItem to="/" title="Home" />}

      {!auth.isLoggedIn && (
        <NavItem onClick={handleDropdownClose} to="/auth" title="Login" />
      )}

      {auth.isLoggedIn && (
        <NavItem onClick={handleProjectsClick} to="/projects" title="Projects" />
      )}

      {auth.isLoggedIn && (
        <NavItem onClick={handleMyTasksClick} to="/tasks" title="My Tasks" />
      )}

      {auth.isLoggedIn && (!!auth.user?.companyId || auth.user?.role === "admin") && (
        <NavItem onClick={handleClientsClick} to="/clients" title="Companies" />
      )}

      {auth.isLoggedIn && auth.user?.role === "admin" && (
        <NavItem onClick={handleAdminClick} to="/admin/companies" title="Admin" />
      )}

      {/* ── Header nav: notification bell (left of the user dropdown) ── */}
      {!isDrawer && auth.isLoggedIn && (
        <li className="nav-links__bell">
          <NotificationBell />
        </li>
      )}

      {/* ── Drawer: settings + logout at bottom ─────────────── */}
      {isDrawer && auth.isLoggedIn && (
        <React.Fragment>
          <li className="drawer-actions-divider" role="separator" />
          <li>
            <button className="nav-button" onClick={handleSettingsClick}>
              Settings
            </button>
          </li>
          <li>
            <button className="nav-button" onClick={handleHelpClick}>
              Help Center
            </button>
          </li>
          <li>
            <button className="nav-button drawer-logout-btn" onClick={handleLogoutClick}>
              Logout
            </button>
          </li>
        </React.Fragment>
      )}

      {/* ── Header nav: user dropdown icon ───────────────────── */}
      {!isDrawer && auth.isLoggedIn && (
        <NavItem
          className="Nav-Item__Button"
          icon="user"
          profileImageUrl={auth.user?.profileImageUrl}
          userInitials={userInitials}
          onOpen={(event) => { toggleUserDropdown(); }}
          ref={dropdownRef}
        >
          {showUserDropDown && (
            <DropDownMenu>
              <DropDownItem onClick={handleSettingsClick}>User Settings</DropDownItem>
              <DropDownItem onClick={handleHelpClick}>Help Center</DropDownItem>
              <DropDownItem onClick={handleLogoutClick}>Logout</DropDownItem>
            </DropDownMenu>
          )}
        </NavItem>
      )}
    </ul>
  );
};

export default React.memo(NavLinks);
